import { delayLabel, estimateFlight } from '@trips/shared/flight-estimate';
import { connection, flightStatus } from '@trips/shared/flight-status';
import { flightMilestones } from '@trips/shared/flight-times';
import { formatDayShort, formatDistance, formatDuration, formatTime } from '@trips/shared/format';
import { distanceMi, interpolateGreatCircle } from '@trips/shared/geo';
import { leaveForAirport } from '@trips/shared/leave-for-airport';
import { inboundPlaneAt, type PlaneFix, planeAt } from '@trips/shared/plane-position';
import type { FlightDetails } from '@trips/shared/schema';
import { localDate, tzAbbr, tzOffsetMinutes } from '@trips/shared/time';
import { lazy, Suspense, useState } from 'react';
import { patch } from '../../data/mutate';
import {
  selectAttachmentsByOwner,
  selectLiveItems,
  selectPlaceLookup,
  selectPrefs,
} from '../../data/selectors';
import { useStore } from '../../data/store';
import { now } from '../../lib/clock';
import { back, navigate } from '../../lib/router';
import { useLiveFlight } from '../../lib/useLiveFlight';
import { Button } from '../../ui/Button';
import { EmptyState } from '../../ui/EmptyState';
import { ErrorBoundary } from '../../ui/ErrorBoundary';
import { Icon } from '../../ui/Icon';
import { RouteArc } from '../../ui/RouteArc';
import { SkyCard } from '../../ui/Sky';
import { StatusPill } from '../../ui/StatusPill';
import { TopBar } from '../../ui/TopBar';
import { Notes } from '../notes/Notes';
import { Photos } from '../photos/Photos';
import { Confirmation } from '../plan/PlanSheet';
import { FlightHistory, LiveBar } from './FlightLive';
import { FlightMap } from './FlightMap';
import type { IncomingPlane } from './FlightMapFull';
import { formatIdent, inboundStatus, YourPlane } from './YourPlane';

const FlightMapFull = lazy(() => import('./FlightMapFull'));

import { phoneLink } from '../../lib/link';
import s from './FlightScreen.module.css';

export function FlightScreen({ itemId }: { itemId: string }) {
  const item = useStore((st) => st.tables.items.get(itemId));
  const live = useStore((st) => st.tables.flightLive.get(itemId));
  const places = useStore(selectPlaceLookup);
  const prefs = useStore(selectPrefs);
  const all = useStore(selectLiveItems);
  const flightsById = useStore((st) => st.tables.flightLive);
  const attachments = useStore((st) => selectAttachmentsByOwner(st).get(itemId)) ?? [];
  useStore((st) => st.minute);
  useLiveFlight(item);
  // The full-screen map, framed on my flight or on the plane flying in for it.
  const [mapFocus, setMapFocus] = useState<'flight' | 'incoming' | null>(null);

  if (!item || item.deleted || item.details.type !== 'flight') {
    return (
      <div>
        <TopBar backTo="/" />
        <EmptyState
          icon="airplane-tilt"
          title="This flight isn't here"
          action={<Button onClick={() => back('/')}>Back to trips</Button>}
        />
      </div>
    );
  }

  const d = item.details as FlightDetails;
  const from = places(item.startPlaceId);
  const to = places(item.endPlaceId);
  const depTz = item.startTz ?? from?.tz ?? 'UTC';
  const arrTz = item.endTz ?? to?.tz ?? depTz;
  const t = now();
  const link = phoneLink();
  const ends = {
    from: from?.lat != null && from.lon != null ? { lat: from.lat, lon: from.lon } : null,
    to: to?.lat != null && to.lon != null ? { lat: to.lat, lon: to.lon } : null,
  };
  const plan =
    item.startAt != null
      ? { startAt: item.startAt, endAt: item.endAt, cancelled: item.status === 'cancelled' }
      : null;
  // Worked out on the phone, so it keeps going in the air with no connection.
  const est = plan ? estimateFlight(plan, live, t, ends, link) : null;
  const dep = est?.out ?? item.startAt;
  const arr = est?.in ?? item.endAt;
  const a = from?.lat != null && from.lon != null ? { lat: from.lat, lon: from.lon } : null;
  const b = to?.lat != null && to.lon != null ? { lat: to.lat, lon: to.lon } : null;
  const progress = est?.progress ?? 0;
  // How far ahead or behind the destination's clock is, right now (daylight saving included).
  const gapMin = tzOffsetMinutes(t, arrTz) - tzOffsetMinutes(t, depTz);
  const zoneGap =
    gapMin === 0 ? null : `${formatDuration(Math.abs(gapMin))} ${gapMin > 0 ? 'ahead' : 'behind'}`;
  // Where my plane is at any moment while it's flying: its last reported position carried on at
  // its speed, or with no report (no wifi) the estimate along the route.
  const planeFn = (n: number): PlaneFix | null => {
    if (!plan || !a || !b) return null;
    const e = estimateFlight(plan, live, n, ends, link);
    if (e.phase !== 'air') return null;
    return planeAt({ from: a, to: b, off: e.off, on: e.on, report: live?.position, now: n });
  };
  const plane = planeFn(t);
  // Before boarding, the plane flying in to operate this flight, when we know where from.
  const here = from?.iata ?? d.origin;
  const inbound =
    live?.inbound &&
    est &&
    (est.phase === 'scheduled' || est.phase === 'boarding') &&
    est.out - t < 12 * 3_600_000
      ? live.inbound
      : null;
  const inOrigin =
    inbound?.originLat != null && inbound.originLon != null
      ? { lat: inbound.originLat, lon: inbound.originLon }
      : null;
  const incoming: IncomingPlane | null =
    inbound && inOrigin && a
      ? {
          from: inOrigin,
          code: inbound.origin ?? '',
          ident: inbound.ident ? formatIdent(inbound.ident) : null,
          status: inboundStatus(inbound, here, depTz, prefs),
          flying: !!inbound.departed && !inbound.landed,
          at: (n) => inboundPlaneAt(inbound, inOrigin, a, n),
        }
      : null;
  const skyPlace =
    plane ??
    (a && b
      ? progress <= 0
        ? a
        : progress >= 1
          ? b
          : interpolateGreatCircle(a, b, progress)
      : (a ?? b));
  const miles = a && b ? distanceMi(a, b) : null;
  const fs = plan ? flightStatus(plan, live, t, ends, link) : null;
  // The next flight out of where this one lands, and whether there's time to make it.
  const onward =
    item.endAt != null
      ? all.find(
          (o) =>
            o.id !== item.id &&
            o.type === 'flight' &&
            o.startPlaceId === item.endPlaceId &&
            o.startAt != null &&
            o.startAt > item.endAt! - 3_600_000 &&
            o.startAt - item.endAt! < 24 * 3_600_000,
        )
      : undefined;
  const onwardLive = onward ? flightsById.get(onward.id) : undefined;
  const conn =
    onward && arr != null && onward.startAt != null
      ? connection(
          arr,
          onwardLive?.estimatedOut ?? onward.startAt,
          !!from?.countryCode &&
            !!places(onward.endPlaceId)?.countryCode &&
            places(onward.endPlaceId)?.countryCode !== to?.countryCode,
        )
      : null;
  const flying = est && (est.phase === 'air' || est.phase === 'landed' || est.phase === 'taxiing');
  const updated =
    est?.updatedAt != null ? `Updated ${formatDuration((t - est.updatedAt) / 60_000)} ago` : null;
  const dash = '–';

  // Check-in, bag drop, boarding and doors, then the flight itself.
  const steps: { label: string; at: number; tz: string; note: string | null }[] = [];
  if (item.startAt != null) {
    const milestones = flightMilestones({
      scheduledDepart: item.startAt,
      expectedDepart: live?.estimatedOut,
      airline: d.airline,
      international:
        !!from?.countryCode && !!to?.countryCode && from.countryCode !== to.countryCode,
      boardingAt: d.boardingAt,
    });
    for (const m of milestones)
      steps.push({
        label: m.label,
        at: m.at,
        tz: depTz,
        note: m.key === 'checkin' ? 'The day before' : m.estimated ? 'Estimated' : null,
      });
    // Going by taxi or Uber from the hotel: when to set off.
    const leave = leaveForAirport(item, all, places);
    if (leave)
      steps.push({
        label: `Leave ${leave.stayName}`,
        at: leave.at,
        tz: depTz,
        note: `${formatDuration(leave.rideMin)} by car`,
      });
    steps.sort((x, y) => x.at - y.at);
    steps.push({
      label: 'Departs',
      at: live?.actualOut ?? dep ?? item.startAt,
      tz: depTz,
      note: live?.actualOut ? 'Actual' : null,
    });
  }
  if (arr != null)
    steps.push({
      label: 'Arrives',
      at: live?.actualIn ?? arr,
      tz: arrTz,
      note: live?.actualIn ? 'Actual' : null,
    });

  const facts: [string, string][] = [
    ['Terminal', live?.terminalOrigin ?? d.terminal ?? dash],
    ['Gate', live?.gateOrigin ?? dash],
    ['Baggage belt', live?.baggageClaim ?? dash],
    ['Arrival terminal', live?.terminalDestination ?? dash],
    ['Arrival gate', live?.gateDestination ?? dash],
    ['Aircraft', live?.aircraftType ?? d.aircraft ?? dash],
    ['Seat', d.seat ?? dash],
    ['Cabin', d.cabin ?? dash],
    ['Tail', live?.registration ?? dash],
  ];
  const cancelled = item.status === 'cancelled' || !!live?.cancelled;
  const diverted = !!live?.diverted;

  return (
    <div className={s.screen}>
      <TopBar title={item.title} backTo={item.tripId ? `/trips/${item.tripId}` : '/'} overlay />
      <SkyCard place={skyPlace} at={t} className={s.hero}>
        <div className={s.heroInner}>
          <div className={s.row}>
            <div>
              <p className={`${s.flight} wide`}>{item.title}</p>
              {item.provider && <p className={s.sub}>{item.provider}</p>}
            </div>
            {fs && <StatusPill label={fs.label} tone={fs.tone} />}
          </div>
          {ends.from && ends.to ? (
            <>
              <FlightMap
                from={ends.from}
                to={ends.to}
                codes={[from?.iata ?? d.origin, to?.iata ?? d.destination]}
                progress={progress}
                plane={plane}
                onOpen={() => setMapFocus('flight')}
              />
              {mapFocus && (
                <ErrorBoundary onError={() => setMapFocus(null)}>
                  <Suspense fallback={null}>
                    <FlightMapFull
                      from={ends.from}
                      to={ends.to}
                      codes={[from?.iata ?? d.origin, to?.iata ?? d.destination]}
                      plane={planeFn}
                      landsAt={est?.phase === 'air' ? est.on : null}
                      incoming={incoming}
                      focus={mapFocus}
                      onClose={() => setMapFocus(null)}
                    />
                  </Suspense>
                </ErrorBoundary>
              )}
              <div className={s.codes}>
                <span className={`${s.code} ${s.small} wide`}>{from?.iata ?? d.origin}</span>
                <span className={`${s.code} ${s.small} wide ${s.right}`}>
                  {to?.iata ?? d.destination}
                </span>
              </div>
            </>
          ) : (
            <div className={s.route}>
              <svg className={s.arc} viewBox="0 0 300 64" aria-hidden="true">
                <RouteArc
                  from={[52, 50]}
                  to={[248, 50]}
                  bulge={0.28}
                  draw
                  strokeWidth={2}
                  className={s.arcInk}
                  progress={progress > 0 ? progress : undefined}
                />
              </svg>
              <div className={s.codes}>
                <span className={`${s.code} wide`}>{from?.iata ?? d.origin}</span>
                <span className={`${s.code} wide ${s.right}`}>{to?.iata ?? d.destination}</span>
              </div>
            </div>
          )}
          <div className={s.times}>
            <div>
              <p className={`${s.time} wide num`}>
                {dep != null ? formatTime(dep, depTz, prefs) : dash}
              </p>
              {fs?.depart.moved && (
                <p className={`${s.was} num`}>{formatTime(fs.depart.scheduled, depTz, prefs)}</p>
              )}
              <p className={`${s.sub} num`}>
                {dep != null
                  ? `${formatDayShort(localDate(dep, depTz), prefs)}, ${tzAbbr(dep, depTz)}`
                  : ''}
              </p>
              <p className={s.sub}>{from?.city}</p>
              {(live?.terminalOrigin ?? d.terminal) && (
                <p className={`${s.sub} num`}>
                  Terminal {live?.terminalOrigin ?? d.terminal}
                  {live?.gateOrigin ? `, gate ${live.gateOrigin}` : ''}
                </p>
              )}
            </div>
            <div className={s.right}>
              <p className={`${s.time} wide num`}>
                {arr != null ? formatTime(arr, arrTz, prefs) : dash}
              </p>
              {fs?.arrive.moved && (
                <p className={`${s.was} num`}>{formatTime(fs.arrive.scheduled, arrTz, prefs)}</p>
              )}
              <p className={`${s.sub} num`}>
                {arr != null
                  ? `${formatDayShort(localDate(arr, arrTz), prefs)}, ${tzAbbr(arr, arrTz)}`
                  : ''}
              </p>
              <p className={s.sub}>{to?.city}</p>
              {live?.terminalDestination && (
                <p className={`${s.sub} num`}>
                  Terminal {live?.terminalDestination}
                  {live?.gateDestination ? `, gate ${live.gateDestination}` : ''}
                </p>
              )}
            </div>
          </div>
          <p className={`${s.sub} num`}>
            {dep != null && arr != null ? formatDuration((arr - dep) / 60_000) : ''}
            {miles != null ? `, ${formatDistance(miles, prefs)}` : ''}
          </p>
          <div className={s.clocks}>
            <span className="num">
              <small>Now in {from?.city ?? from?.iata ?? d.origin}</small>
              {formatTime(t, depTz, prefs)} {tzAbbr(t, depTz)}
            </span>
            <span className={`${s.right} num`}>
              <small>Now in {to?.city ?? to?.iata ?? d.destination}</small>
              {formatTime(t, arrTz, prefs)} {tzAbbr(t, arrTz)}
              {zoneGap ? <em>{zoneGap}</em> : null}
            </span>
          </div>
        </div>
      </SkyCard>

      <div className={s.body}>
        {inbound && (
          <YourPlane
            inbound={inbound}
            here={here}
            tz={depTz}
            prefs={prefs}
            now={t}
            onMap={incoming && ends.to ? () => setMapFocus('incoming') : undefined}
          />
        )}
        {(cancelled || diverted || fs?.notices.length || (conn && conn.risk !== 'ok')) && (
          <ul className={s.notices}>
            {cancelled && (
              <li data-risk="missed">
                Cancelled. Check the {item.provider ?? 'airline'} app for a rebooking, then use
                Change flight below.
              </li>
            )}
            {diverted && (
              <li data-risk="missed">
                Diverted{live?.divertedTo ? ` to ${live.divertedTo}` : ''}.
              </li>
            )}
            {fs?.notices.map((n) => (
              <li key={n}>{n}</li>
            ))}
            {conn && conn.risk !== 'ok' && (
              <li data-risk={conn.risk}>
                {conn.label}
                {onward ? `, ${onward.title}` : ''}
              </li>
            )}
          </ul>
        )}
        {flying && est && (
          <div className={s.inflight}>
            <div className={s.inflightMain}>
              <span className="t-footnote muted">
                {est.phase === 'air'
                  ? 'Lands in'
                  : est.phase === 'landed'
                    ? 'At the gate in'
                    : 'Takeoff'}
              </span>
              <span className={`${s.inflightBig} num`}>
                {est.phase === 'taxiing'
                  ? formatTime(est.off, depTz, prefs)
                  : formatDuration(est.minutesLeft ?? 0)}
              </span>
            </div>
            <div className={s.inflightSide}>
              <span className="num">
                Arrives {formatTime(est.in, arrTz, prefs)} {tzAbbr(est.in, arrTz)}
              </span>
              <span className="num">{delayLabel(est.arriveDelay)}</span>
              {est.milesLeft != null && (
                <span className="num">{formatDistance(est.milesLeft, prefs)} to go</span>
              )}
            </div>
            <div className={s.inflightNote}>
              <p className="t-footnote muted">
                {est.source === 'position'
                  ? `From the plane's position${updated ? `. ${updated}` : ''}`
                  : est.source === 'last-known'
                    ? `Estimated on this phone from the last update${updated ? `. ${updated}` : ''}`
                    : est.source === 'schedule'
                      ? 'Estimated on this phone from the schedule'
                      : (updated ?? 'Live')}
              </p>
              {plane && (
                <button type="button" className={s.mapBtn} onClick={() => setMapFocus('flight')}>
                  <Icon name="map-trifold" size={16} />
                  Map
                </button>
              )}
            </div>
          </div>
        )}
        <div className={s.facts}>
          {facts.map(([k, v]) => (
            <div key={k} className={s.fact}>
              <span className="t-footnote muted">{k}</span>
              <span className={`${s.factValue} num`}>{v}</span>
            </div>
          ))}
        </div>
        <LiveBar
          itemId={item.id}
          live={live}
          now={t}
          tz={depTz}
          prefs={prefs}
          date={item.startAt != null ? localDate(item.startAt, depTz) : ''}
        />
        {item.confirmation && <Confirmation code={item.confirmation} />}

        <section className={s.section}>
          <h3 className="t-subhead">At the airport</h3>
          <ol className={s.events}>
            {steps.map((e) => (
              <li key={e.label} className={s.event}>
                <span className={s.eventDot} data-done={e.at <= t || undefined} />
                <span className={s.eventLabel}>{e.label}</span>
                <span className={`${s.eventTime} num`}>
                  {formatTime(e.at, e.tz, prefs)}
                  {e.note && <small>{e.note}</small>}
                </span>
              </li>
            ))}
          </ol>
        </section>

        <FlightHistorySection itemId={item.id} depTz={depTz} arrTz={arrTz} prefs={prefs} now={t} />

        <section className={s.section}>
          <h3 className="t-subhead">Notes</h3>
          <Notes value={item.notes} onSave={(notes) => void patch('items', item.id, { notes })} />
        </section>
        <section className={s.section}>
          <h3 className="t-subhead">Boarding pass and files</h3>
          <Photos ownerType="item" ownerId={item.id} photos={attachments} />
        </section>
        <div className={s.actions}>
          <Button
            icon="pencil-simple"
            onClick={() => navigate(`/plans/${item.id}/edit`, { overlay: true })}
          >
            Edit flight
          </Button>
          {item.tripId && (
            <Button
              variant="ghost"
              icon="suitcase-rolling"
              onClick={() => navigate(`/trips/${item.tripId}`)}
            >
              Open trip
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/** The change log, under a heading only when there's something in it. */
function FlightHistorySection(props: Parameters<typeof FlightHistory>[0]) {
  const any = useStore((st) =>
    [...st.tables.itemChanges.values()].some((c) => c.itemId === props.itemId && !c.deleted),
  );
  if (!any) return null;
  return (
    <section className={s.section}>
      <h3 className="t-subhead">Changes</h3>
      <FlightHistory {...props} />
    </section>
  );
}
